import { observer } from 'mobx-react';
import React, { useCallback, useMemo, useState } from 'react';
import DealsItem from '../../components/DealsItem/DealsItem';
import FakeDealsItem from '../../components/FakeDealsItem/FakeDealsItem';
import dealsStore from '../../store/dealsStore';
import { Icons } from 'src/assets/fonts/Icons/icons';
import theme from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import { modalStore } from 'src/mobx/modalStore';
import { emptyArray } from 'src/utils/helpers';
import { TOOLTIP_DURATION, TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Text, { TEXT } from 'src/view/components/Text/Text';
import './style.scss';

const DealsItems: React.FC = observer((): JSX.Element | null => {
  const [fakeItemsCount, setFakeItemsCount] = useState<number>(2);

  const openItemsModal = useCallback(() => {
    if (!dealsStore.deal) return;
    modalStore.openItemsForDealModal({
      selectedItems: dealsStore.deal.items,
      onSave: dealsStore.addItem,
      isMulti: true,
    });
  }, []);

  const deleteFakeItem = useCallback(() => {
    setFakeItemsCount(dealsStore.deal?.items?.length ? 0 : fakeItemsCount - 1);
  }, [fakeItemsCount]);

  const renderFakeItems = useCallback((): JSX.Element[] | JSX.Element | null => {
    if (!dealsStore.deal?.is_draft) return null;

    if (!dealsStore.deal?.items?.length) {
      return emptyArray(fakeItemsCount).map((item, index) => <FakeDealsItem key={index} onDelete={deleteFakeItem} />);
    }
    if (dealsStore.deal.items?.length === 1 && fakeItemsCount) return <FakeDealsItem onDelete={deleteFakeItem} />;

    return null;
  }, [deleteFakeItem, fakeItemsCount]);

  const itemsColumnsTitles = useMemo((): boolean => {
    if (dealsStore.deal?.is_draft) return Boolean(fakeItemsCount || dealsStore.deal?.items?.length);
    return Boolean(dealsStore.deal?.items?.length);
  }, [fakeItemsCount, dealsStore.deal?.items?.length]);

  if (!dealsStore.deal) return null;

  return (
    <div className="deals-items mt-6 d-flex flex-column">
      <Text type={TEXT.SECTION_HEADER} text="combo_offer_items" />
      <div className="d-flex flex-column">
        {/* items */}
        <ul className="d-flex flex-column mt-4">
          {itemsColumnsTitles ? (
            <div className="d-flex flex-row">
              <div className="deals-item__input mr-3 d-flex flex-row align-items-center">
                <Text
                  type={TEXT.BLOCK_HEADER}
                  text="combo_offer_item"
                  required
                  tooltip={{
                    message: 'dc_nosize_nomodifier',
                    duration: TOOLTIP_DURATION.EXTRA_LARGE,
                    position: TOOLTIP_POSITION.FIXED,
                    maxWidth: 380,
                    iconSize: 16,
                  }}
                />
              </div>
              <div className="deals-item__input mr-3 d-flex flex-row align-items-center">
                <Text
                  type={TEXT.BLOCK_HEADER}
                  text="discounted_price"
                  required
                  tooltip={{
                    message: 'discounted_price_autocalc',
                    duration: TOOLTIP_DURATION.EXTRA_LARGE,
                    position: TOOLTIP_POSITION.FIXED,
                    iconSize: 16,
                  }}
                />
              </div>
              <Text type={TEXT.BLOCK_HEADER} text="item_price" />
            </div>
          ) : null}
          {dealsStore.deal.items?.map((item) => (
            <DealsItem key={item.id} item={item} />
          ))}
          {renderFakeItems()}
        </ul>
        {/* add item button */}
        <div className="deals-items__button-add-wrapper mt-4">
          <button className="deals-items__button-add" onClick={openItemsModal}>
            <div className="deals-items__button-add-icon">
              <Icon name={Icons.Plus} size={11} color={theme.colors.mainblue} />
            </div>
            <p className="family-medium text-size-16 color-mainblue ml-2">{localeStore.t('add_item')}</p>
          </button>
        </div>
        {/* validation */}
        {!dealsStore.validation.itemsLength ? <Text type={TEXT.ERROR} text="at_least_2_items" /> : null}
        {!dealsStore.validation.pricesBalance ? <Text type={TEXT.ERROR} text="dcidp_exceeds_dcp_error" /> : null}
      </div>
    </div>
  );
});

export default DealsItems;
